import { Component, inject, Type, TemplateRef } from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ModalService, ModalRef, ConfirmVariant } from '../../core/services/modal.service';
import { UiButtonComponent } from '../button/ui-button.component';

@Component({
  selector: 'ui-modal-host',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet, UiButtonComponent],
  template: `
    @for (modal of modalService.modals(); track modal.id) {
      <div class="ui-modal-layer" [class.ui-modal-layer--confirm]="isConfirm(modal)">
        <div class="ui-modal-layer__backdrop" (click)="onMaskClick(modal)"></div>

        @if (isConfirm(modal)) {
          <div
            class="ui-confirm"
            role="alertdialog"
            aria-modal="true"
            [attr.aria-labelledby]="'ui-confirm-title-' + modal.id"
            [attr.aria-describedby]="'ui-confirm-desc-' + modal.id">
            @if (modal.config.closable !== false) {
              <button
                type="button"
                class="ui-confirm__dismiss"
                aria-label="Fechar"
                (click)="onConfirmCancel(modal)">×</button>
            }

            <div
              class="ui-confirm__icon"
              [class.ui-confirm__icon--danger]="confirmVariant(modal) === 'danger'"
              [class.ui-confirm__icon--default]="confirmVariant(modal) === 'default'"
              aria-hidden="true">
              @if (confirmVariant(modal) === 'danger') {
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              } @else {
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4M12 8h.01" stroke-linecap="round"/>
                </svg>
              }
            </div>

            @if (modal.config.title) {
              <h2 class="ui-confirm__title" [id]="'ui-confirm-title-' + modal.id">{{ modal.config.title }}</h2>
            }

            @if (isString(modal.config.content)) {
              <p class="ui-confirm__message" [id]="'ui-confirm-desc-' + modal.id">{{ modal.config.content }}</p>
            }

            <div class="ui-confirm__footer">
              <ui-button variant="secondary" [emphasis]="true" (clicked)="onConfirmCancel(modal)">
                {{ cancelLabel(modal) }}
              </ui-button>
              <ui-button
                [variant]="confirmOkVariant(modal)"
                [emphasis]="true"
                (clicked)="onConfirmOk(modal)">
                {{ okLabel(modal) }}
              </ui-button>
            </div>
          </div>
        } @else {
          <div
            role="dialog"
            aria-modal="true"
            class="ui-modal"
            [style.max-width]="modal.config.width">
            @if (modal.config.title) {
              <div class="ui-modal__header">
                <h2 class="ui-modal__title">{{ modal.config.title }}</h2>
                @if (modal.config.closable !== false) {
                  <button type="button" class="ui-modal__close" aria-label="Fechar" (click)="modal.close()">×</button>
                }
              </div>
            }
            <div class="ui-modal__body">
              @if (isString(modal.config.content)) {
                <p class="ui-modal__text">{{ modal.config.content }}</p>
              } @else if (isTemplate(modal.config.content)) {
                <ng-container *ngTemplateOutlet="modal.config.content" />
              } @else if (modal.config.content) {
                <ng-container *ngComponentOutlet="modal.config.content" />
              }
            </div>
          </div>
        }
      </div>
    }
  `,
  styles: [`
    .ui-modal-layer {
      position: fixed;
      inset: 0;
      z-index: 9998;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem;
    }

    .ui-modal-layer--confirm {
      padding: 1.25rem;
    }

    .ui-modal-layer__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(15, 23, 42, 0.5);
      backdrop-filter: blur(3px);
      animation: ui-modal-backdrop-in 0.2s ease-out;
    }

    @keyframes ui-modal-backdrop-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    /* —— Confirm —— */
    .ui-confirm {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: 28rem;
      padding: 1.5rem 1.5rem 1.25rem;
      border-radius: 0.875rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-bg, #fff);
      box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
      text-align: center;
      animation: ui-confirm-in 0.22s cubic-bezier(0.2, 0.9, 0.24, 1);
    }

    @keyframes ui-confirm-in {
      from {
        opacity: 0;
        transform: translateY(8px) scale(0.97);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .ui-confirm__dismiss {
      position: absolute;
      top: 0.65rem;
      right: 0.65rem;
      width: 2rem;
      height: 2rem;
      border: none;
      border-radius: 0.5rem;
      background: transparent;
      color: var(--text-muted, #8c8c8c);
      font-size: 1.35rem;
      line-height: 1;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;
    }

    .ui-confirm__dismiss:hover {
      background: var(--surface-muted, #f5f5f5);
      color: var(--text-body, #434343);
    }

    .ui-confirm__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 3rem;
      height: 3rem;
      margin: 0 auto 1rem;
      border-radius: 999px;
    }

    .ui-confirm__icon svg {
      width: 1.5rem;
      height: 1.5rem;
    }

    .ui-confirm__icon--danger {
      background: color-mix(in srgb, var(--danger, #ff4d4f) 14%, var(--surface-bg));
      color: var(--danger, #ff4d4f);
      border: 1px solid color-mix(in srgb, var(--danger, #ff4d4f) 30%, var(--border-color));
    }

    .ui-confirm__icon--default {
      background: color-mix(in srgb, var(--brand-primary, #1677ff) 12%, var(--surface-bg));
      color: var(--brand-primary, #1677ff);
      border: 1px solid color-mix(in srgb, var(--brand-primary, #1677ff) 24%, var(--border-color));
    }

    .ui-confirm__title {
      margin: 0 0 0.5rem;
      font-size: 1.0625rem;
      font-weight: 600;
      line-height: 1.35;
      color: var(--text-body, #434343);
    }

    .ui-confirm__message {
      margin: 0 0 1.25rem;
      font-size: 0.875rem;
      line-height: 1.55;
      color: var(--text-muted, #8c8c8c);
      text-wrap: pretty;
    }

    .ui-confirm__footer {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: center;
      padding-top: 0.25rem;
      border-top: 1px solid var(--border-color, #e8e8e8);
    }

    .ui-confirm__footer ui-button {
      flex: 1;
      min-width: min(100%, 7.5rem);
    }

    @media (min-width: 400px) {
      .ui-confirm__footer {
        justify-content: flex-end;
      }

      .ui-confirm__footer ui-button {
        flex: 0 1 auto;
      }
    }

    /* —— Modal genérico —— */
    .ui-modal {
      position: relative;
      z-index: 1;
      width: 100%;
      max-height: 90vh;
      overflow: auto;
      border-radius: 0.75rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-bg, #fff);
      box-shadow: 0 20px 40px rgba(15, 23, 42, 0.15);
      animation: ui-confirm-in 0.2s ease-out;
    }

    .ui-modal__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid var(--border-color, #e8e8e8);
    }

    .ui-modal__title {
      margin: 0;
      font-size: 1.0625rem;
      font-weight: 600;
      color: var(--text-body, #434343);
    }

    .ui-modal__close {
      border: none;
      background: transparent;
      color: var(--text-muted, #8c8c8c);
      font-size: 1.35rem;
      line-height: 1;
      cursor: pointer;
      padding: 0.15rem 0.35rem;
      border-radius: 0.35rem;
    }

    .ui-modal__close:hover {
      color: var(--text-body, #434343);
      background: var(--surface-muted, #f5f5f5);
    }

    .ui-modal__body {
      padding: 1.25rem;
    }

    .ui-modal__text {
      margin: 0;
      font-size: 0.875rem;
      line-height: 1.55;
      color: var(--text-body, #434343);
    }
  `],
})
export class UiModalHostComponent {
  modalService = inject(ModalService);

  isString(v: unknown): v is string { return typeof v === 'string'; }
  isTemplate(v: unknown): v is TemplateRef<unknown> { return v instanceof TemplateRef; }

  isConfirm(modal: ModalRef): boolean {
    return !!modal.config.data?.['confirm'];
  }

  confirmVariant(modal: ModalRef): ConfirmVariant {
    return modal.config.data?.['confirmVariant'] === 'default' ? 'default' : 'danger';
  }

  confirmOkVariant(modal: ModalRef): 'primary' | 'danger' {
    return this.confirmVariant(modal) === 'danger' ? 'danger' : 'primary';
  }

  okLabel(modal: ModalRef): string {
    return String(modal.config.data?.['okLabel'] ?? 'Confirmar');
  }

  cancelLabel(modal: ModalRef): string {
    return String(modal.config.data?.['cancelLabel'] ?? 'Cancelar');
  }

  onMaskClick(modal: ModalRef): void {
    if (modal.config.maskClosable !== false) {
      if (this.isConfirm(modal)) {
        this.onConfirmCancel(modal);
      } else {
        modal.close();
      }
    }
  }

  onConfirmOk(modal: ModalRef): void {
    const fn = modal.config.data?.['onOk'];
    if (typeof fn === 'function') (fn as () => void)();
  }

  onConfirmCancel(modal: ModalRef): void {
    const fn = modal.config.data?.['onCancel'];
    if (typeof fn === 'function') (fn as () => void)();
    else modal.close();
  }
}
